import React, {useMemo, Suspense, lazy} from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import Skeleton from '@material-ui/lab/Skeleton';
import Accordion from 'features/POSRegisters/components/Accordion';
import Device from 'features/POSRegisters/interfaces/Device';
import {IpadScanDetails} from 'features/POSRegisters/interfaces/ScanDetails';
import {withAlerts} from 'features/POSRegisters/POSTable/Registers/components/TableWithAlerts';
import {AlertScanSubType} from 'features/POSRegisters/interfaces/AlertInfo';

const TableWithAlerts = lazy(
  () => import('features/POSRegisters/POSTable/Registers/components/TableWithAlerts')
);

//#region styles
const StyledContainer = styled(Grid)`
  padding: 8px;
  gap: 8px;

  > .MuiPaper-root {
    background: transparent;
  }

  .MuiTableCell-root.MuiTableCell-head {
    padding-top: 4px;
    padding-bottom: 0;
  }

  .MuiTableCell-root.MuiTableCell-head span {
    font-size: 12px;
  }

  .MuiTableCell-root.MuiTableCell-body p {
    font-size: 16px;
  }
`;

const StyledSkeleton = styled(Skeleton)`
  height: 300px;
  background-color: ${props => props.theme.text}0D;
`;

const StyledAccordion = styled(Accordion)`
  padding: 0;

  > .MuiAccordionSummary-root {
    height: 24px;
    padding: 0 8px;
    font-size: 16px;
    font-weight: 700;
    color: ${props => props.theme.text};
    text-transform: uppercase;
  }

  &.neutral > .MuiAccordionSummary-root {
    background: ${props => props.theme.disabledStateBlue}33;
  }

  &.transparent > .MuiAccordionSummary-root {
    background: transparent;
    padding: 8px 0 0;
  }
`;

const StyledHeader = styled(Grid)`
  max-height: 24px;
  padding: 4px 8px;
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.theme.text};
  text-transform: uppercase;
  background: ${props => props.theme.disabledStateBlue}33;

  &.transparent {
    background: transparent;
    padding: 8px 0 0;
  }
`;

const StyledTableContainer = styled(Grid)`
  padding: 0 32px;
`;
//#endregion styles

interface WithConditionalWrapperProps {
  children: React.ReactNode;
  className?: string;
  title: string;
  wrapper: 'accordion' | 'default';
  arrowPlacement?: 'start' | 'end';
}

function WithConditionalWrapper(props: WithConditionalWrapperProps) {
  const {children, className, title, wrapper, arrowPlacement} = props;
  switch (wrapper) {
    case 'accordion':
      return (
        <StyledAccordion
          className={className}
          title={title}
          arrowPlacement={arrowPlacement}
          expanded={true}
        >
          {children}
        </StyledAccordion>
      );
    default:
      return (
        <Grid container>
          <StyledHeader item className={className} xs={12}>
            {title}
          </StyledHeader>
          <Grid item xs={12}>
            {children}
          </Grid>
        </Grid>
      );
  }
}

const customRowsPerPage = {
  defaultRowsPerPage: 5,
  rowsPerPageOptions: [5, 10, 20, {label: 'All', value: -1}],
};

const connectivityColumns = [
  {
    id: 'deviceName',
    label: 'DEVICE NAME',
    width: '10rem',
  },
  {
    id: 'ipAddress',
    label: 'IP ADDRESS',
    width: '8rem',
  },
  {
    id: 'vlanNumber',
    label: 'VLAN',
    width: '100px',
  },
  {
    id: 'switchId',
    label: 'Switch ID',
    width: '100px',
  },
  {
    id: 'switchNumber',
    label: 'Switch Number',
    width: '120px',
  },
  {
    id: 'portNumber',
    label: 'Port Number',
    width: '100px',
  },
];

const connectivityCheckInColumns = [
  {
    id: 'deviceName',
    label: 'DEVICE NAME',
    width: '10rem',
  },
  {
    id: 'mobileApiLastSeen',
    label: 'Mobile Api Last Seen',
    width: '220px',
  },
  {
    id: 'registerFirstSeenDate',
    label: 'Register First Seen Date',
    width: '220px',
  },
  {
    id: 'registerLastSeenDate',
    label: 'Register Last Seen Date',
    width: '220px',
  },
];

const nposVersionColumns = [
  {
    id: 'deviceName',
    label: 'DEVICE NAME',
    width: '10rem',
  },
  {
    id: 'registerAppVersion',
    label: 'Version',
    width: '10rem',
  },
  {
    id: 'workstationNumber',
    label: 'Workstation Number',
    width: '10rem',
  },
];

const operatingSystemColumns = [
  {
    id: 'deviceName',
    label: 'DEVICE NAME',
    width: '10rem',
  },
  {
    id: 'iosVersion',
    label: 'iOS VERSION',
    width: '10rem',
  },
];

const hardwareColumns = [
  {
    id: 'deviceName',
    label: 'DEVICE NAME',
    width: '10rem',
  },
  {
    id: 'manufacturer',
    label: 'MANUFACTURER',
    width: '8rem',
  },
  {
    id: 'model',
    label: 'MODEL',
    width: '14rem',
  },
  {
    id: 'macAddress',
    label: 'PRIMARY MAC ADDRESS',
    width: '12rem',
  },
  {
    id: 'serialNumber',
    label: 'SERIAL NUMBER',
    width: '12rem',
  },
  {
    id: 'ipAddress',
    label: 'IP ADDRESS',
    width: '8rem',
  },
];

interface IpadsProps {
  ipads: Device<IpadScanDetails>[];
}

export default function Ipads({ipads}: IpadsProps) {
  const wrapper = useMemo(() => (ipads.length > 5 ? 'accordion' : 'default'), [ipads]);

  return (
    <StyledContainer container>
      <WithConditionalWrapper className="neutral" title="Connectivity" wrapper={wrapper}>
        <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
          <StyledTableContainer>
            <TableWithAlerts
              paginationText={'Devices'}
              customRowsPerPage={customRowsPerPage}
              columns={connectivityColumns}
              data={withAlerts(ipads, AlertScanSubType.CONNECTIVITY)}
              noDataText={'No devices were found.'}
              loaded={true}
              disablePagination={wrapper === 'default'}
            />
          </StyledTableContainer>
        </Suspense>
      </WithConditionalWrapper>
      <WithConditionalWrapper className="neutral" title="Check-In Status" wrapper={wrapper}>
        <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
          <StyledTableContainer>
            <TableWithAlerts
              paginationText={'Devices'}
              customRowsPerPage={customRowsPerPage}
              columns={connectivityCheckInColumns}
              data={withAlerts(ipads, AlertScanSubType.CHECK_IN_STATUS)}
              noDataText={'No devices were found.'}
              loaded={true}
              disablePagination={wrapper === 'default'}
            />
          </StyledTableContainer>
        </Suspense>
      </WithConditionalWrapper>

      <WithConditionalWrapper className="neutral" title="Information" wrapper={wrapper}>
        <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
          <StyledTableContainer>
            <WithConditionalWrapper
              className="transparent"
              title="NPOS"
              arrowPlacement="start"
              wrapper={wrapper}
            >
              <TableWithAlerts
                paginationText={'Stores'}
                customRowsPerPage={customRowsPerPage}
                columns={nposVersionColumns}
                data={withAlerts(ipads, AlertScanSubType.NPOS)}
                noDataText={'No stores were found.'}
                loaded={true}
                disablePagination={wrapper === 'default'}
              />
            </WithConditionalWrapper>
          </StyledTableContainer>
        </Suspense>

        <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
          <StyledTableContainer>
            <WithConditionalWrapper
              className="transparent"
              title="OPERATING SYSTEM"
              arrowPlacement="start"
              wrapper={wrapper}
            >
              <TableWithAlerts
                paginationText={'Devices'}
                customRowsPerPage={customRowsPerPage}
                columns={operatingSystemColumns}
                data={withAlerts(ipads, AlertScanSubType.OPERATING_SYSTEM)}
                noDataText={'No devices were found.'}
                loaded={true}
                disablePagination={wrapper === 'default'}
              />
            </WithConditionalWrapper>
          </StyledTableContainer>
        </Suspense>

        <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
          <StyledTableContainer>
            <WithConditionalWrapper
              className="transparent"
              title="HARDWARE"
              arrowPlacement="start"
              wrapper={wrapper}
            >
              <TableWithAlerts
                paginationText={'devices'}
                customRowsPerPage={customRowsPerPage}
                columns={hardwareColumns}
                data={withAlerts(ipads, AlertScanSubType.HARDWARE)}
                noDataText={'No devices were found.'}
                loaded={true}
                disablePagination={wrapper === 'default'}
              />
            </WithConditionalWrapper>
          </StyledTableContainer>
        </Suspense>
      </WithConditionalWrapper>
    </StyledContainer>
  );
}
